﻿import isAfter from 'date-fns/isAfter';
import isWithinInterval from 'date-fns/isWithinInterval';
import { IOrderListItem } from '../../../../Entities';
import { IOrderCollection, OrderCollectionsWithDate, OrdersWithTitle } from '../types';

export function filterOrdersOnBrokerAgent(
    selectedBrokerAgent: number
): (order: IOrderListItem) => boolean {
    return function filter(order: IOrderListItem): boolean {
        if (selectedBrokerAgent > 0) {
            return order.brokerAgentId === selectedBrokerAgent;
        }

        return true;
    };
}

export function filterOrdersOnOrderType(
    selectedOrderType: number
): (order: IOrderListItem) => boolean {
    return function filter(order: IOrderListItem): boolean {
        if (selectedOrderType > -1) {
            return order.orderType.value === selectedOrderType;
        }

        return true;
    };
}

export function filterOrdersOnFreeTextSearch(
    searchPhrase: string
): (order: IOrderListItem) => boolean {
    return function filter(order: IOrderListItem): boolean {
        let hasMatches = false;

        if (`${order.orderCollectionNumber}:${order.orderNumber}`.includes(searchPhrase)) {
            hasMatches = true;
        }

        const address = order.address ? order.address.toLowerCase() : '';
        const city = order.city ? order.city.toLowerCase() : '';

        if (`${address}, ${city}`.includes(searchPhrase.toLowerCase())) {
            hasMatches = true;
        }

        const packageProduct = order.packageProduct ? order.packageProduct.toLowerCase() : '';
        if (packageProduct.includes(searchPhrase.toLowerCase())) {
            hasMatches = true;
        }

        const photographer = order.photographer ? order.photographer.toLowerCase() : '';
        if (photographer.includes(searchPhrase.toLowerCase())) {
            hasMatches = true;
        }

        return hasMatches;
    };
}

function createOrderPlacedAtDateInterval(
    orderPlacedAtStart: Date | null,
    orderPlacedAtEnd: Date | null,
    earliestAvailableDate: Date
): Interval {
    if (!orderPlacedAtStart && orderPlacedAtEnd) {
        const orderDateStart = earliestAvailableDate.setHours(0, 0, 0, 0);

        return {
            start: orderDateStart,
            end: orderPlacedAtEnd.setHours(0, 0, 0, 0)
        };
    }

    if (orderPlacedAtStart && !orderPlacedAtEnd) {
        const currentDate = new Date().setHours(0, 0, 0, 0);

        return {
            start: orderPlacedAtStart.setHours(0, 0, 0, 0),
            end: currentDate
        };
    }

    return {
        start: orderPlacedAtStart.setHours(0, 0, 0, 0),
        end: orderPlacedAtEnd.setHours(0, 0, 0, 0)
    };
}

export function filterOrdersOnPlacedAtDate(
    orderPlacedAtStart: Date | null,
    orderPlacedAtEnd: Date | null,
    earliestAvailableDate: Date
): (order: IOrderListItem) => boolean {
    return function filter(order: IOrderListItem): boolean {
        if (!orderPlacedAtStart && !orderPlacedAtEnd) {
            return true;
        }

        if (isAfter(orderPlacedAtStart, orderPlacedAtEnd)) {
            return false;
        }

        const orderDate = new Date(order.orderPlacedAt).setHours(0, 0, 0, 0);

        const interval = createOrderPlacedAtDateInterval(
            orderPlacedAtStart,
            orderPlacedAtEnd,
            earliestAvailableDate
        );

        return isWithinInterval(orderDate, interval);
    };
}

export function transformOrderCollectionToOrders(
    orderCollectionsWithDate: OrderCollectionsWithDate
): OrdersWithTitle {
    const { orderCollections } = orderCollectionsWithDate;

    const orders = orderCollections.reduce((acc, orderCollection): IOrderListItem[] => {
        return acc.concat(orderCollection.orders);
    }, []);

    return {
        title: orderCollectionsWithDate.title,
        orders
    };
}

export function filterOrderCollectionOnOrderPlacedAtDate(
    orderPlacedAtStart: Date | null,
    orderPlacedAtEnd: Date | null,
    earliestAvailableDate: Date
): (orderCollection: IOrderCollection) => boolean {
    return function filter(orderCollection: IOrderCollection): boolean {
        if (!orderPlacedAtStart && !orderPlacedAtEnd) {
            return true;
        }

        return orderCollection.orders.some(
            filterOrdersOnPlacedAtDate(orderPlacedAtStart, orderPlacedAtEnd, earliestAvailableDate)
        );
    };
}
